import { useBoolean, useToast } from '@chakra-ui/react'
import * as crypto from 'crypto-js'
import dayjs from 'dayjs'
import fileDownload from 'js-file-download'
import { createContext, FC, useCallback, useMemo, useState } from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import useSessions from '@hook/shared/use-sessions'

import KpiModal from '@component/Modules/Kpi/Modal'

import { actionsHeaderKpi } from '@helper/kpi/sales/actionsHeaderKpi'
import { disableActionsByDefaultModal } from '@helper/kpi/sales/disableActionsByDefaultModal'
import { formatParamsModalKpiToSend } from '@helper/kpi/sales/formatParamsModalKpiToSend'
import { formattedDate } from '@helper/kpi/sales/formattedDateKpi'
import { paramsToSendMapping } from '@helper/kpi/sales/paramsToSendMapping'
import { parseSalesKpiData } from '@helper/kpi/sales/parseSalesKpiData'
import { parseSalesKpiDataModal } from '@helper/kpi/sales/parseSalesKpiDataModal'
import { parseSalesKpiExportData } from '@helper/kpi/sales/parseSalesKpiExportData'
import { validateGroupName } from '@helper/kpi/sales/validateGroupName'
import { validateIfShowColumnInTable } from '@helper/kpi/sales/validateIfShowColumnInTable'
import {
  decrypterHeaderStorange,
  encrypteHeaderStorange
} from '@helper/util/cryptoStorange'
import generic from '@service/generic'
import kpi from '@service/kpi'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import {
  IDefaultDate,
  IGraphicResponse,
  IKpiSalesContext,
  ILineParams,
  IParamsToSend,
  IParseParamsModalOrGraphic,
  IUpdateParams,
  Pagination
} from '@base/@types/providers/Kpi/SalesProvider'
import { kpiModalTableStructure } from '@base/variables/headers/kpi/sales/kpiModalTableStructure'
import { kpiSalesHeader } from '@base/variables/headers/kpi/sales/kpiSalesHeader'
import { saleIndicatorsRequest } from '@base/variables/headers/kpi/sales/saleIndicatorsRequest'
import { salesFieldsFilter } from '@base/variables/headers/kpi/sales/salesFieldsFilter'

enum DaysQuantity {
  year = 365
}

enum ChartType {
  real = '1',
  btleo = '2'
}

const KpiSalesProvider: FC = ({ children }) => {
  const toast = useToast()
  const { register } = useSessions()
  const filtersList: ILayoutFilter[] = salesFieldsFilter

  const [isModalConfig, setIsModalConfig] = useState(false)
  const [currentyDate, setCurrentyDate] = useState({} as IDefaultDate)
  const [stateLineData, setStateLineData] = useState({} as ILineParams)
  const [selectRealOrBtleo, setSelectRealOrBtleo] = useState('1')
  const [isGraphic, setIsGraphic] = useState(false)
  const [isShowContentChart, setIsShowContentChart] = useState(false)
  const [graphicValues, setGraphicValues] = useState({} as IGraphicResponse)
  const [selected, setSelected] = useState(false)
  const [isModal, setIsModal] = useState(false)
  const [isLoadingExport, setIsLoadingExport] = useState(false)
  const [filters, setFilters] = useBoolean(true)
  const [query, setQuery] = useState('')
  const [filename, setFilename] = useState('KPI Vendas')
  const [loading, setLoading] = useState(false)
  const [loadingChart, setLoadingChart] = useState(false)
  const [loadingModal, setLoadingModal] = useState(false)
  const [fetching, _setFetching] = useState(false)
  const [data, updateData] = useImmer<any[]>([])
  const [modalData, updateModalData] = useImmer<any[]>([])
  const [params, updateParams] = useImmer<any>(saleIndicatorsRequest)
  const [headers, updateHeaders] = useImmer<ITableHeader[]>(kpiSalesHeader)

  const [paramsGraphicToSend, setParamsGraphicToSend] =
    useState<IParamsToSend>()

  const [modalHeaders, _updateModalHeaders] = useImmer<ITableHeader[]>(
    kpiModalTableStructure
  )
  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })

  const defaultDate = useMemo(() => {
    const dateNow = new Date()
    const dateEnd = dateNow.setDate(dateNow.getDate() - DaysQuantity.year)
    const initialDate = dayjs(dateEnd).format('YYYY-MM-DD')
    const finalDate = dayjs(new Date()).format('YYYY-MM-DD')

    setCurrentyDate({
      initialDate,
      finalDate
    })

    return {
      initialDate,
      finalDate
    } as IDefaultDate
  }, [])

  const [paramsToSend, setParamsToSend] = useState<IParamsToSend>(() => {
    const paramsFormatted = { ...saleIndicatorsRequest }

    paramsFormatted.periodo = {
      agrupar: paramsFormatted.periodo.agrupar,
      inicio: formattedDate(defaultDate.initialDate),
      final: formattedDate(defaultDate.finalDate)
    }

    return paramsFormatted
  })

  const updateIsModalConfig = () => {
    setIsModalConfig(!isModalConfig)
  }

  const handleSetSelectRealOrBtleo = (value: string) => {
    setSelectRealOrBtleo(value)
  }

  const handleGraphicIsVisible = (value: boolean) => {
    setIsGraphic(value)
  }

  const getDataGraphic = useCallback(
    (paramsToSendFormatted: IParamsToSend, value?: string) => {
      setIsShowContentChart(true)
      setLoadingChart(true)

      kpi
        .postSaleIndicatorGraphicData(
          paramsToSendFormatted || paramsToSend,
          value
        )
        .then(({ data: { data } }) => {
          setGraphicValues(data)
          setLoadingChart(false)
        })
        .catch(({ response: { data } }) => {
          setIsGraphic(false)
          setLoadingChart(false)
          setIsShowContentChart(false)
          setGraphicValues({ margem: [], vendas: [] })

          const isData = data.message === 'Nenhum dado encontrado'

          toast({
            title: isData ? 'Dados não encontrados.' : data.message,
            description: 'Não há dados para que possa ser montado o gráfico',
            status: isData ? 'info' : 'error',
            duration: 4000,
            isClosable: true,
            position: 'top-right'
          })
        })
    },
    [paramsToSend, toast]
  )

  const getDataModal = useCallback(
    (paramsToSendFormatted: IParamsToSend) => {
      setParamsGraphicToSend(paramsToSendFormatted)

      kpi
        .getSaleIndicatorProductsData(paramsToSendFormatted || paramsToSend)
        .then(({ data: { data } }) => {
          updateModalData(data)
          setLoadingModal(false)
          setIsModal(true)
        })
        .catch(() => setLoadingModal(false))
    },
    [paramsToSend, updateModalData]
  )

  const parseParamsModalOrGraphic = ({
    isClickLine,
    lineData,
    value
  }: IParseParamsModalOrGraphic) => {
    if (localStorage.getItem('@kpiSalesParamsToSend:')) {
      const paramsToSendSaved = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('@kpiSalesParamsToSend:'),
          'CHAVE'
        ).toString(crypto.enc.Utf8)
      )

      !value && setSelectRealOrBtleo(ChartType.real)

      const paramsToSendFormatted = formatParamsModalKpiToSend(
        lineData || stateLineData,
        paramsToSendSaved || paramsToSend
      )

      if (!isClickLine) {
        return getDataModal(paramsToSendFormatted)
      }

      getDataGraphic(paramsToSendFormatted, value)
    }
  }

  const updateIsGraphic = (lineData: ILineParams) => {
    setIsGraphic(true)
    setStateLineData(lineData)

    parseParamsModalOrGraphic({
      isClickLine: true,
      lineData
    })
  }

  const handleSelected = (lineData: ILineParams) => {
    setSelected(!!lineData)
    setStateLineData(lineData)
    setLoadingModal(true)

    if (lineData) {
      parseParamsModalOrGraphic({
        isClickLine: false,
        lineData
      })
    }
  }

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'icon',
        icon: <AiOutlineBarChart />,
        onSelect: handleSelected
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const tableInteractionModal = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'icon',
        onFocus: true,
        icon: <AiOutlineBarChart />,
        onSelect: () => []
      }
    ]
  }, [])

  const handleStorange = useCallback(async () => {
    if (localStorage.getItem('@kpiSalesColumnsTable:')) {
      const newHeader = decrypterHeaderStorange('@kpiSalesColumnsTable:')
      updateHeaders(newHeader)
    }
  }, [updateHeaders])

  const handleHeaderLocalStorange = useCallback(() => {
    if (!localStorage.getItem('@kpiSalesColumnsTable:')) {
      encrypteHeaderStorange('@kpiSalesColumnsTable:', headers)
    } else {
      localStorage.removeItem('@kpiSalesColumnsTable:')

      encrypteHeaderStorange('@kpiSalesColumnsTable:', headers)
    }
  }, [headers])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show,
        notShowColumn: newHeaders[index].columns[column].notShowColumn
          ? !newHeaders[index].columns[column].notShowColumn
          : true,
        isNumeric: newHeaders[index].columns[column].isNumeric
      }

      updateHeaders(newHeaders)
      handleHeaderLocalStorange()
    },
    [handleHeaderLocalStorange, headers, updateHeaders]
  )

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return actionsHeaderKpi({
      data,
      headers,
      toggleColumn,
      parseSalesKpiData
    })
  }, [toggleColumn, data, headers])

  const updateShowTableInTable = (data: any[]) => {
    updateData(draft => {
      return draft.filter(item => item.status === 99).concat(data)
    })

    const headersFormatted = validateIfShowColumnInTable(headers, paramsToSend)

    handleStorange()
    updateHeaders(headersFormatted)
  }

  const clearAllFilters = () => {
    const paramsFormatted = { ...saleIndicatorsRequest }

    paramsFormatted.periodo = {
      agrupar: paramsFormatted.periodo.agrupar,
      inicio: formattedDate(defaultDate.initialDate),
      final: formattedDate(defaultDate.finalDate)
    }

    updateParams({
      ...params,
      empresaIds: false,
      empresaSelectIds: [],
      centroCdLojaIds: false,
      centroCdLojaSelectIds: [],
      gerenteCategoriaIds: false,
      gerenteCategoriaSelectIds: [],
      gerenteProdutoIds: false,
      gerenteProdutoSelectIds: [],
      mundoIds: false,
      mundoSelectIds: [],
      categoriaIds: false,
      categoriaSelectIds: [],
      categoriaCompactaIds: false,
      categoriaCompactaSelectIds: [],
      statusIds: false,
      statusSelectIds: [],
      curvaAbcIds: false,
      curvaAbcSelectIds: [],
      fornecedorSelectIds: [],
      periodoIds: false
    })

    setParamsToSend(paramsFormatted)

    setCurrentyDate({
      initialDate: defaultDate.initialDate,
      finalDate: defaultDate.finalDate
    })
    if (localStorage.getItem('kpiSalesParams')) {
      localStorage.setItem(
        'kpiSalesParams',
        crypto.AES.encrypt(
          JSON.stringify({
            ...params,
            empresaIds: false,
            empresaSelectIds: [],
            centroCdLojaIds: false,
            centroCdLojaSelectIds: [],
            gerenteCategoriaIds: false,
            gerenteCategoriaSelectIds: [],
            gerenteProdutoIds: false,
            gerenteProdutoSelectIds: [],
            mundoIds: false,
            mundoSelectIds: [],
            categoriaIds: false,
            categoriaSelectIds: [],
            categoriaCompactaIds: false,
            categoriaCompactaSelectIds: [],
            statusIds: false,
            statusSelectIds: [],
            curvaAbcIds: false,
            curvaAbcSelectIds: [],
            fornecedorSelectIds: [],
            periodoIds: false
          }),
          'CHAVE'
        ).toString()
      )
    }
    if (localStorage.getItem('kpiSalesParamsRequest')) {
      localStorage.setItem(
        'kpiSalesParamsRequest',
        crypto.AES.encrypt(JSON.stringify(paramsFormatted), 'CHAVE').toString()
      )
    }
  }

  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    updateParams((draft: { [x: string]: string }) => {
      draft[name] = value
    })

    name === 'initialDate' &&
      setCurrentyDate({ initialDate: value, finalDate: currentyDate.finalDate })

    name === 'finalDate' &&
      setCurrentyDate({
        finalDate: value,
        initialDate: currentyDate.initialDate
      })
    const paramsToSendFormatted = paramsToSendMapping({
      name,
      value,
      compareName: name,
      paramsToSend,
      fieldName: validateGroupName(name)
    })

    setParamsToSend(paramsToSendFormatted)
    if (localStorage.getItem('kpiSalesParams')) {
      const newParams: IParamsToSend[] = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('kpiSalesParams'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )

      localStorage.setItem(
        'kpiSalesParams',
        crypto.AES.encrypt(
          JSON.stringify({ ...newParams, [name]: value }),
          'CHAVE'
        ).toString()
      )
    }
    if (localStorage.getItem('kpiSalesParamsRequest')) {
      localStorage.setItem(
        'kpiSalesParamsRequest',
        crypto.AES.encrypt(
          JSON.stringify(paramsToSendFormatted),
          'CHAVE'
        ).toString()
      )
    }
  }

  const handleLocalParams = useCallback(async () => {
    if (localStorage.getItem('kpiSalesParams')) {
      const newParams = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('kpiSalesParams'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )
      updateParams(newParams)
    } else {
      localStorage.setItem(
        'kpiSalesParams',
        crypto.AES.encrypt(JSON.stringify(params), 'CHAVE').toString()
      )
    }
    if (localStorage.getItem('kpiSalesParamsRequest')) {
      const newParams = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('kpiSalesParamsRequest'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )
      setParamsToSend(newParams)
    } else {
      localStorage.setItem(
        'kpiSalesParamsRequest',
        crypto.AES.encrypt(JSON.stringify(paramsToSend), 'CHAVE').toString()
      )
    }
  }, [updateParams, setParamsToSend, params, paramsToSend])

  const handlePageChange = useCallback(
    async (_page: number) => {
      setLoading(true)

      localStorage.setItem(
        '@kpiSalesParamsToSend:',
        crypto.AES.encrypt(JSON.stringify(paramsToSend), 'CHAVE').toString()
      )
      let newParams = {}
      if (localStorage.getItem('kpiSalesParamsRequest')) {
        newParams = JSON.parse(
          crypto.AES.decrypt(
            localStorage.getItem('kpiSalesParamsRequest'),
            'CHAVE'
          ).toString(crypto.enc.Utf8) || ''
        )
      } else {
        newParams = paramsToSend
      }
      kpi
        .getSaleIndicatorsData(newParams, _page)
        .then(({ data: { data, pagination } }) => {
          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(data)
          })
          updatePagination(pagination)
          setLoading(false)
        })
        .catch(() => {
          updateData([])
          setLoading(false)
        })
    },
    [paramsToSend, updateData, updatePagination]
  )
  const getData = () => {
    setGraphicValues({ margem: [], vendas: [] })
    setIsShowContentChart(false)
    getMaintence()

    setIsGraphic(false)
    setLoading(true)

    localStorage.setItem(
      '@kpiSalesParamsToSend:',
      crypto.AES.encrypt(JSON.stringify(paramsToSend), 'CHAVE').toString()
    )
    let newParams = {}
    if (localStorage.getItem('kpiSalesParamsRequest')) {
      newParams = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('kpiSalesParamsRequest'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )
    } else {
      newParams = paramsToSend
    }

    kpi
      .getSaleIndicatorsData(newParams, 1)
      .then(({ data: { data, pagination } }) => {
        updateShowTableInTable(data)
        updatePagination(pagination)
        localStorage.setItem(
          '@kpiSalesParamsResponse:',
          crypto.AES.encrypt(JSON.stringify(data), 'CHAVE').toString()
        )

        register({
          name: 'KPI Vendas',
          path: '/kpi/vendas',
          loading: false
        })
        setFilters.toggle()
        setLoading(false)
      })
      .catch(() => {
        updateData([])
        setLoading(false)
      })
  }

  const exportKpiData = useCallback(() => {
    Swal.fire({
      title: 'Exporta dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })

    const paramsToSendFormatted = parseSalesKpiExportData(headers, paramsToSend)

    kpi
      .postSaleIndicatorExportData(paramsToSendFormatted)
      .then(({ data }) => {
        fileDownload(data, filename + '.csv')
        Swal.close()
      })
      .catch(({ response: { data } }) => {
        const message =
          data.response?.status === 400
            ? data.response?.data?.data[0].message
            : 'Ocorreu um problema interno. Tente novamente mais tarde'
        toast({
          title: 'Atenção.',
          description: message,
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
      })
  }, [filename, headers, paramsToSend, toast])

  const exportKpiDataModal = useCallback(() => {
    setIsLoadingExport(true)

    const paramsToSendFormatted = { ...paramsGraphicToSend }

    kpi
      .postSaleIndicatorExportModalData(paramsToSendFormatted)
      .then(({ data }) => {
        fileDownload(data, filename + '.csv')
        setIsLoadingExport(false)
      })
      .catch(({ response: { data } }) => {
        setIsLoadingExport(false)

        const message =
          data.response?.status === 400
            ? data.response?.data?.data[0].message
            : 'Ocorreu um problema interno. Tente novamente mais tarde'
        toast({
          title: 'Atenção.',
          description: message,
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
      })
  }, [filename, paramsGraphicToSend, toast])

  const getMaintence = useCallback(async () => {
    generic
      .getMaintence('manutencao')
      .then(({ data: { data } }) => {
        if (data.valor === 'true') {
          return (window.location.href = '/manutencao')
        }
      })
      .catch(() => {})
  }, [])

  return (
    <KpiSalesContext.Provider
      value={{
        data: parseSalesKpiData(data),
        query,
        params,
        isModal,
        actions,
        headers,
        filters,
        selected,
        filename,
        loading,
        fetching,
        isGraphic,
        filtersList,
        defaultDate,
        currentyDate,
        handleLocalParams,
        loadingModal,
        loadingChart,
        graphicValues,
        isModalConfig,
        selectRealOrBtleo,
        isShowContentChart,
        tableInteractionModal,
        setQuery,
        getData,
        exportKpiData,
        clearAllFilters,
        setFilters,
        setSelected,
        setFilename,
        toggleColumn,
        updateIsGraphic,
        updateIsModalConfig,
        pagination,
        handlePageChange,
        tableInteraction,
        handleUpdateParams,
        handleGraphicIsVisible,
        parseParamsModalOrGraphic,
        handleSetSelectRealOrBtleo
      }}
    >
      {children}
      <KpiModal
        id="sales-table-modal"
        entity="kpi-sales-modal"
        data={parseSalesKpiDataModal(modalData)}
        isOpen={selected}
        onClose={() => setSelected(!selected)}
        headers={modalHeaders}
        query={query}
        itsSortable={true}
        loadingModal={loadingModal}
        actions={tableInteractionModal}
        isModalConfig={isModalConfig}
        updateIsModalConfig={updateIsModalConfig}
        kpiModalTableStructure={kpiModalTableStructure}
        isDisabled={disableActionsByDefaultModal}
        onClickExport={exportKpiDataModal}
        isLoadingExport={isLoadingExport}
        isExport
        isPaged
      />
    </KpiSalesContext.Provider>
  )
}

export default KpiSalesProvider
export const KpiSalesContext = createContext<IKpiSalesContext>(
  {} as IKpiSalesContext
)
